
import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Define the feature type for better type safety
interface Feature {
  name: string;
  reason: string;
}

const SwitchPackages = () => {
  const { t } = useTranslation('index');
  
  // Define the packages data
  const packages = [
    {
      title: t('packages.quickStart.title'),
      description: t('packages.quickStart.description'),
      shortDesc: t('packages.quickStart.shortDesc'),
      features: t('packages.quickStart.features', { returnObjects: true }) as Feature[],
      path: "/paket/snabbstart",
      timeEstimate: t('packages.quickStart.timeEstimate'),
      id: "snabbstart"
    },
    {
      title: t('packages.everyday.title'),
      description: t('packages.everyday.description'),
      shortDesc: t('packages.everyday.shortDesc'),
      features: t('packages.everyday.features', { returnObjects: true }) as Feature[],
      path: "/paket/vardagspaketet",
      timeEstimate: t('packages.everyday.timeEstimate'),
      id: "vardagspaketet"
    },
    {
      title: t('packages.fullSwitch.title'),
      description: t('packages.fullSwitch.description'),
      shortDesc: t('packages.fullSwitch.shortDesc'),
      features: t('packages.fullSwitch.features', { returnObjects: true }) as Feature[],
      path: "/paket/full-frikoppling",
      timeEstimate: t('packages.fullSwitch.timeEstimate'),
      id: "full-frikoppling"
    }
  ];

  return (
    <section className="bg-white py-16">
      <div className="container-custom">
        <h2 className="text-3xl font-bold text-center text-frikopplad-navy mb-2">{t('packages.title')}</h2>
        <p className="text-center text-gray-600 mb-10">{t('packages.subtitle')}</p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {packages.map((pkg, index) => (
            <Card key={index} className={`shadow-md hover:shadow-lg transition-shadow relative ${pkg.id === "sociala-media" || pkg.id === "snabbstart" || pkg.id === "vardagspaketet" || pkg.id === "full-frikoppling" ? "isolation-auto" : ""}`}>
              {pkg.id === "sociala-media" && (
                <div 
                  className="absolute inset-0 opacity-10 bg-contain bg-no-repeat bg-center pointer-events-none"
                  style={{
                    backgroundImage: "url('https://upload.wikimedia.org/wikipedia/commons/d/dd/Mastodon_mascot_vector_version.svg')"
                  }}
                ></div>
              )}
              {pkg.id === "snabbstart" && (
                <div 
                  className="absolute inset-0 opacity-10 bg-contain bg-no-repeat bg-center pointer-events-none"
                  style={{
                    backgroundImage: "url('https://upload.wikimedia.org/wikipedia/commons/7/7a/Firefox_brand_logo%2C_2019.svg')"
                  }}
                ></div>
              )}
              {pkg.id === "vardagspaketet" && (
                <div 
                  className="absolute inset-0 opacity-10 bg-contain bg-no-repeat bg-center pointer-events-none"
                  style={{
                    backgroundImage: "url('https://upload.wikimedia.org/wikipedia/commons/6/60/Nextcloud_Logo.svg')"
                  }}
                ></div>
              )}
              {pkg.id === "full-frikoppling" && (
                <div 
                  className="absolute inset-0 opacity-10 bg-contain bg-no-repeat bg-center pointer-events-none"
                  style={{
                    backgroundImage: "url('https://upload.wikimedia.org/wikipedia/commons/d/d8/Tux-flat.svg')"
                  }}
                ></div>
              )}
              <CardHeader>
                <CardTitle className="text-xl font-bold">{pkg.title}</CardTitle>
                <CardDescription>{pkg.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-700 mb-4">{pkg.shortDesc}</p>
                <div className="space-y-3">
                  {Array.isArray(pkg.features) && pkg.features.map((feature, i) => (
                    <div key={i} className="flex items-start space-x-2">
                      <div className="h-5 w-5 mt-0.5 rounded-full bg-frikopplad-blue/20 flex items-center justify-center text-frikopplad-blue">
                        <span className="text-xs font-bold">{i+1}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium">{feature.name}</p>
                        <p className="text-xs text-gray-500">{feature.reason}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {pkg.timeEstimate}
                  </p>
                </div>
              </CardContent>
              <CardFooter>
                <Button className="w-full bg-frikopplad-blue hover:bg-frikopplad-navy" asChild>
                  <Link to={pkg.path}>
                    {t('packages.button')} {pkg.title.toLowerCase()} <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SwitchPackages;
